"use client";

import React, { useCallback, useState } from "react";
import { Eraser, Copy, PanelRight, RotateCcw, Search } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/shadcn-components/ui/context-menu";
import { useTerminalStore } from "@/lib/terminal/store";
import DslExplorerDialog from "./DslExplorerDialog";

/**
 * Wraps the terminal surface with a native-feeling right-click menu.
 * Headline action opens the DSL Explorer; the rest are quick actions on
 * the live terminal state (re-run last command, copy last output, clear).
 */
export default function TerminalContextMenu({
  children,
}: {
  children: React.ReactNode;
}) {
  const [explorerOpen, setExplorerOpen] = useState(false);
  const runCommand = useTerminalStore((s) => s.runCommand);
  const clear = useTerminalStore((s) => s.clear);
  const history = useTerminalStore((s) => s.commandHistory);
  const entries = useTerminalStore((s) => s.entries);
  const explorerDocked = useTerminalStore((s) => s.explorerDocked);
  const setExplorerDocked = useTerminalStore((s) => s.setExplorerDocked);

  const lastCommand = history[history.length - 1];

  const runLast = useCallback(() => {
    if (lastCommand) void runCommand(lastCommand);
  }, [lastCommand, runCommand]);

  const copyLastOutput = useCallback(() => {
    for (let i = entries.length - 1; i >= 0; i -= 1) {
      const entry = entries[i];
      if (entry.output != null) {
        void navigator.clipboard?.writeText(entry.output);
        return;
      }
      if (entry.error) {
        void navigator.clipboard?.writeText(entry.error.message);
        return;
      }
    }
  }, [entries]);

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent className="w-52">
          <ContextMenuItem onClick={() => setExplorerOpen(true)}>
            <Search className="mr-2 h-3.5 w-3.5 text-orange-500" />
            Explore DSL…
          </ContextMenuItem>
          <ContextMenuItem onClick={() => setExplorerDocked(!explorerDocked)}>
            <PanelRight className="mr-2 h-3.5 w-3.5" />
            {explorerDocked
              ? "Hide DSL explorer"
              : "Show DSL explorer in terminal"}
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem disabled={!lastCommand} onClick={runLast}>
            <RotateCcw className="mr-2 h-3.5 w-3.5" />
            Run last again
          </ContextMenuItem>
          <ContextMenuItem
            disabled={entries.length === 0}
            onClick={copyLastOutput}
          >
            <Copy className="mr-2 h-3.5 w-3.5" />
            Copy last output
          </ContextMenuItem>
          <ContextMenuItem
            disabled={entries.length === 0}
            onClick={() => clear()}
          >
            <Eraser className="mr-2 h-3.5 w-3.5" />
            Clear terminal
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      <DslExplorerDialog open={explorerOpen} onOpenChange={setExplorerOpen} />
    </>
  );
}
